import { call, put, takeLatest, select } from 'redux-saga/effects';
import axios from 'axios';
import {
  fetchCartRequest,
  fetchCartSuccess,
  fetchCartFailure,
  addToCartRequest,
  addToCartSuccess,
  addToCartFailure,
  updateCartItemRequest,
  updateCartItemSuccess,
  updateCartItemFailure,
  removeFromCartRequest,
  removeFromCartSuccess,
  removeFromCartFailure,
  clearCartRequest,
  clearCartSuccess,
  clearCartFailure,
} from '../slices/cartSlice';
import { showLoading, hideLoading } from '../slices/loadingSlice'; // ✅ ADD THIS

const getToken = (state) => state.auth.token;
const API_BASE_URL =  process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api/customer/cart`;


function* getAuthHeaders() {
  const token = yield select(getToken);
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
}

function transformCartResponse(data) {
  if (!data || !data.items) {
    return { items: [], totalItems: 0, totalPrice: 0 };
  }

  const items = data.items.map(item => ({
    id: item.id,
    pickleId: item.pickle?.id || item.pickleId,
    pickleName: item.pickle?.name || item.pickleName || 'Unknown',
    pickleImageUrl: item.pickle?.imageUrl || item.pickleImageUrl || '/placeholder-pickle.jpg',
    price: item.price || 0,
    quantity: item.quantity || 0,
    selectedWeight: item.selectedWeight || 250,
    subtotal: item.subtotal || (item.price * item.quantity) || 0,
  }));

  const totalItems = data.totalItems || items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = data.totalPrice || items.reduce((sum, item) => sum + item.subtotal, 0);

  return { items, totalItems, totalPrice };
}

function* fetchCartSaga() {
  try {
    yield put(showLoading('Loading cart...')); // ✅ ADD THIS
    
    const headers = yield call(getAuthHeaders);
    const response = yield call(axios.get, API_BASE, headers);
    const transformedData = transformCartResponse(response.data);
    yield put(fetchCartSuccess(transformedData));
  } catch (error) {
    console.error('Fetch cart error:', error.response || error);
    yield put(fetchCartFailure(error.response?.data?.message || 'Failed to fetch cart'));
  } finally {
    yield put(hideLoading()); // ✅ ADD THIS
  }
}

function* addToCartSaga(action) {
  try {
    yield put(showLoading('Adding to cart...')); // ✅ ADD THIS
    
    const headers = yield call(getAuthHeaders);
    const response = yield call(
      axios.post,
      API_BASE,
      action.payload,
      headers
    );
    const transformedData = transformCartResponse(response.data);
    yield put(addToCartSuccess(transformedData));
    yield call(fetchCartSaga);
  } catch (error) {
    console.error('Add to cart error:', error.response || error);
    yield put(addToCartFailure(error.response?.data?.message || 'Failed to add to cart'));
    throw error;
  } finally {
    yield put(hideLoading()); // ✅ ADD THIS
  }
}

function* updateCartItemSaga(action) {
  try {
    yield put(showLoading('Updating cart...')); // ✅ ADD THIS
    
    const { cartItemId, quantity } = action.payload;
    const headers = yield call(getAuthHeaders);
    const response = yield call(
      axios.put,
      `${API_BASE}/${cartItemId}`,
      { quantity },
      headers
    );
    const transformedData = transformCartResponse(response.data);
    yield put(updateCartItemSuccess(transformedData));
    yield call(fetchCartSaga);
  } catch (error) {
    console.error('Update cart item error:', error.response || error);
    yield put(updateCartItemFailure(error.response?.data?.message || 'Failed to update cart'));
    throw error;
  } finally {
    yield put(hideLoading()); // ✅ ADD THIS
  }
}

function* removeFromCartSaga(action) {
  try {
    yield put(showLoading('Removing item...')); // ✅ ADD THIS
    
    const cartItemId = action.payload;
    const headers = yield call(getAuthHeaders);
    yield call(axios.delete, `${API_BASE}/${cartItemId}`, headers);
    yield call(fetchCartSaga);
  } catch (error) {
    console.error('Remove from cart error:', error.response || error);
    yield put(removeFromCartFailure(error.response?.data?.message || 'Failed to remove from cart'));
    throw error;
  } finally {
    yield put(hideLoading()); // ✅ ADD THIS
  }
}

function* clearCartSaga() {
  try {
    yield put(showLoading('Clearing cart...')); // ✅ ADD THIS
    
    const headers = yield call(getAuthHeaders);
    yield call(axios.delete, API_BASE, headers);
    yield put(clearCartSuccess());
  } catch (error) {
    console.error('Clear cart error:', error.response || error);
    yield put(clearCartFailure(error.response?.data?.message || 'Failed to clear cart'));
    throw error;
  } finally {
    yield put(hideLoading()); // ✅ ADD THIS
  }
}

export default function* cartSaga() {
  yield takeLatest(fetchCartRequest.type, fetchCartSaga);
  yield takeLatest(addToCartRequest.type, addToCartSaga);
  yield takeLatest(updateCartItemRequest.type, updateCartItemSaga);
  yield takeLatest(removeFromCartRequest.type, removeFromCartSaga);
  yield takeLatest(clearCartRequest.type, clearCartSaga);
}
